import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import DatePicker from "../../components/common/DatePicker";
import KycSection from "../../components/kyc/KycSection";
import MerchantProfile from "../profile/MerchantProfile";
import AadhaarKycWidget from "../../components/kyc/AadhaarKycWidget";
import KycIframe from "../../components/kyc/KycIframe";
import { uploadKycDocuments, submitKyc, getKycStatus, initiateAadhaarKyc } from "../../services/kycService";
import { handleApiError } from "../../utils/handleApiError";
import { useKyc } from "../../context/KycContext";
import { getProfile } from "../../services/authService";
import api from "../../services/api";

const BUSINESS_TYPES = [
   "Services", "Food & Beverage", "Healthcare", "Education",
  "E-commerce", "Manufacturing", "Logistics", "Real Estate", "Other",
];

function Field({ label, name, value, onChange, maxLength, placeholder }) {
  return (<>
    <div>
      <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">
        {label}
      </label>
      <input
        name={name}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        placeholder={placeholder}
        className="w-full rounded-[12px] p-[14px] text-[14px] text-[#333] bg-white placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors"
      />
    </div>
  </>);
}

function SelectField({ label, name, value, onChange }) {
  return (
    <div>
      <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">{label}</label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-[12px] p-[14px] text-[14px]  text-[#333] bg-white outline-none border border-gray-200 focus:border-violet-400 transition-colors cursor-pointer"
      />
    </div>
  );
}

const steps = [
  { number: 1, title: "Personal Details", subtitle: "STEP 01" },
  { number: 2, title: "Business Info",    subtitle: "STEP 02" },
  { number: 3, title: "Banking & KYC",    subtitle: "STEP 03" },
];

export default function OnboardMerchant({ onNext }) {
  const { refreshKycStatus, kycStatus } = useKyc();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [kycFiles, setKycFiles] = useState({});
  const kycFilesRef = useRef({});
  const [loading, setLoading] = useState(false);
  
  // KYC Modal states
  const [showKycModal, setShowKycModal] = useState(false);
  const [signingUrl, setSigningUrl] = useState("");
  const [kycApiLoading, setKycApiLoading] = useState(false);
  
  const [form, setForm] = useState({
    aadhaar: "", pan: "", name: "", dob: "",
    businessName: "", businessAddress: "", businessType: "", gstNumber: "",
    bankName: "", accountNumber: "", ifsc: "", branchName: ""
  });

  // PAN verification
  const [panVerified, setPanVerified] = useState(false);
  const [panLoading, setPanLoading] = useState(false);
  const [panMsg, setPanMsg] = useState(null); // { type: "success"|"error", text }

  // Aadhaar verification
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [livenessVerified, setLivenessVerified] = useState(false);

  // Digio verification status
  const [digioStatus, setDigioStatus] = useState(null);

  // KYC API Functions
  const initiateKycVerification = async (userEmail = null) => {
    setKycApiLoading(true);
    try {
      const customerIdentifier = userEmail || "";
      const dob = form.dob || "01/01/1990";
      
      const response = await initiateAadhaarKyc(customerIdentifier, dob);
      const data = response.data;
      
      if (data.success && data.data && data.data.signing_url) {
        setSigningUrl(data.data.signing_url);
        setShowKycModal(true);
      } else {
        toast.error('KYC initiation failed. Please try again.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to initiate KYC';
      toast.error(errorMsg);
    } finally {
      setKycApiLoading(false);
    }
  };

  const closeKycModal = () => {
    setShowKycModal(false);
    setSigningUrl("");
  };

  const fetchDigioStatus = () => {
    getKycStatus()
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;

        setDigioStatus({
          aadhaar: { verified: d.aadhaar?.verified, data: { aadhaar_number: d.aadhaar?.aadhaar_number, name: d.aadhaar?.name, dob: d.aadhaar?.dob } },
          pan:     { verified: d.pan?.verified,     data: { pan_number: d.pan?.pan_number, name: d.personal?.full_name, dob: d.personal?.dob } },
          gst:     { verified: d.gst?.verified },
        });

        if (d.aadhaar?.verified) {
          let dob = d.aadhaar?.dob || "";
          if (dob && dob.includes("/")) {
            const [dd, mm, yyyy] = dob.split("/");
            dob = `${yyyy}-${mm}-${dd}`;
          }
          setForm((prev) => ({
            ...prev,
            ...(d.aadhaar.name         && { name:    d.aadhaar.name }),
            ...(dob                    && { dob }),
            ...(d.aadhaar.aadhaar_number && { aadhaar: d.aadhaar.aadhaar_number }),
          }));
        }

        if (d.pan?.verified) {
          setForm((prev) => ({
            ...prev,
            ...(d.pan.pan_number && { pan: d.pan.pan_number }),
          }));
        }

        if (d.gst?.verified) {
          setForm((prev) => ({
            ...prev,
            ...(d.gst.gst_number    && { gstNumber:       d.gst.gst_number }),
            ...(d.gst.business_name && { businessName:    d.gst.business_name }),
            ...(d.gst.business_address && { businessAddress: d.gst.business_address }),
            ...(d.gst.business_type && { businessType:    d.gst.business_type }),
          }));
        }

        if (d.pan?.verified)     setPanVerified(true);
        if (d.aadhaar?.verified) setAadhaarVerified(true);
        if (d.gst?.verified)     setGstVerified(true);
        if (d.liveness?.verified) setLivenessVerified(true);
      })
      .catch(() => {});
  };

  // run only once on mount
  useEffect(() => { fetchDigioStatus(); }, []);

  // Default step 3 if reupload request
  useEffect(() => {
    if (kycStatus?.reupload_request?.status === "pending") {
      setCurrentStep(3);
    }
  }, [kycStatus?.reupload_request?.status]);

  // GST verification
  const [gstVerified, setGstVerified] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMsg, setGstMsg] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Convert PAN to uppercase
    const processedValue = name === "pan" ? value.toUpperCase() : value;
    setForm((prev) => ({ ...prev, [name]: processedValue }));
    if (name === "pan") { setPanVerified(false); setPanMsg(null); }
    if (name === "gstNumber") { setGstVerified(false); setGstMsg(null); }
  };

  const verifyPan = async () => {
    if (!digioStatus?.aadhaar?.verified) {
      setPanMsg({ type: "error", text: "Please complete Aadhaar verification first." });
      return;
    }
    if (!form.pan) {
      setPanMsg({ type: "error", text: "Enter PAN number first." });
      return;
    }else if(!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan)) {
      setPanMsg({ type: "error", text: "Invalid PAN number format." });
      return;
    }
    setPanLoading(true);
    setPanMsg(null);
    try {
      const fd = new FormData();
      const [y, m, d] = form.dob.split("-");
      fd.append("pan_number", form.pan);
      fd.append("name", form.name);
      fd.append("dob", `${d}/${m}/${y}`);
      const res = await api.post("/kyc/verify-pan", fd);
      const data = res.data?.data;
      if (data?.name) setForm((prev) => ({ ...prev, name: data.name }));
      setPanVerified(true);
      setPanMsg({ type: "success", text: "PAN Verified ✅" });
    } catch (err) {
      const msg = err.response?.data?.message || "PAN verification failed.";
      setPanMsg({ type: "error", text: msg });
    } finally {
      setPanLoading(false);
    }
  };

  const verifyGst = async () => {
    if (!form.gstNumber) {
      setGstMsg({ type: "error", text: "Enter GST Number first." });
      return;
    }
    setGstLoading(true);
    setGstMsg(null);
    try {
      const fd = new FormData();
      fd.append("gst_number", form.gstNumber);
      const res = await api.post("/kyc/verify-gst", fd);
      const data = res.data?.data;
      if (data?.business_name || data?.business_address || data?.business_type)
        setForm((prev) => ({
          ...prev,
          ...(data.business_name    && { businessName:    data.business_name }),
          ...(data.business_address && { businessAddress: data.business_address }),
          ...(data.business_type    && { businessType:    data.business_type }),
        }));
      setGstVerified(true);
      setGstMsg({ type: "success", text: "GST Verified ✅" });
    } catch (err) {
      const msg = err.response?.data?.message || "GST verification failed.";
      setGstMsg({ type: "error", text: msg });
    } finally {
      setGstLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1 && !panVerified) {
      toast.error("Please verify your PAN before proceeding.");
      return;
    }
    if (currentStep === 2 && !gstVerified) {
      toast.error("Please verify your GST before proceeding.");
      return;
    }
    const isReupload = kycStatus?.reupload_request?.status === "pending";
    if (currentStep === 3 && !isReupload && !livenessVerified) {
      toast.error("Please complete liveness verification before submitting.");
      return;
    }
    if (currentStep < 3) { setCurrentStep(currentStep + 1); return; }
    // Step 3 — reupload: only documents API, normal: both APIs
    setLoading(true);
    try {
      await uploadKycDocuments(kycFilesRef.current);
      if (!isReupload) await submitKyc(form);
      toast.success("KYC submitted successfully!");
      setSubmitted(true);
      await refreshKycStatus();
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.data && typeof errData.data === "object") {
        Object.values(errData.data).forEach((msgs) => {
          if (Array.isArray(msgs)) msgs.forEach((m) => toast.error(m));
        });
      } else {
        handleApiError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  if (submitted) return <MerchantProfile />;

  const sectionHeader = (icon, title, subtitle) => (
    <div className="flex items-center gap-2.5 mb-5">
      <div className="w-[34px] h-[34px] rounded-[12px] bg-[#4B23641A] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[18px] font-bold text-[#191C1D]">{title}</div>
        <div className="text-[14px] text-[#4C444E]">{subtitle}</div>
      </div>
    </div>
  );

  const renderStepIndicator = () => (
    <div className="flex items-center mb-7">
      {steps.map((step, index) => (
        <div key={step.number} className={`flex items-center ${index < steps.length - 1 ? "flex-1" : ""}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm
              ${currentStep === step.number ? "bg-[linear-gradient(135deg,_#EA8534_0%,_#A0111A_100%)] text-white"
              : currentStep > step.number  ? "border-[#CEC3D0] bg-[#E1E3E4] shadow-[0px_4px_6px_-4px_#57290033,0px_10px_15px_-3px_#57290033] text-[#4C444E] border-2"
              : "bg-[#E1E3E4] border-[#CEC3D0] text-[#4C444E] border-2"}`}>
              {step.number}
            </div>
            <div>
              <div className={`text-[13px] font-bold tracking-wide
                ${currentStep === step.number ? "text-[#4C444E]"
                : currentStep > step.number  ? "text-[#34094D]"
                : "text-[#4C444E]"}`}>
                {step.subtitle}
              </div>
              <div className={`text-[15px] font-bold
                ${currentStep >= step.number ? "text-[#4C444E]" : "text-[#4C444E]"}`}>
                {step.title}
              </div>
            </div>
          </div>
          {index < steps.length - 1 && (
            <div className={`flex-1 h-px mx-5 ${currentStep > step.number ? "bg-green-600" : "bg-gray-200"}`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="bg-[#F3F4F5] shadow-[0px_1px_2px_0px_#0000000D] rounded-[24px] px-5 pt-5 pb-6 mb-5">
            {sectionHeader(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#4B2364" stroke="#4B2364" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>,
              "Personal Details",
              "Identity information of the merchant owner"
            )}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {/* Aadhaar Verification */}
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Aadhaar Verification</label>
                {digioStatus?.aadhaar?.verified ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={digioStatus?.aadhaar?.data?.aadhaar_number || "xxxx xxxx xxxx"}
                      readOnly
                      className="flex-1 rounded-[12px] p-[14px] text-[14px] text-[#333] bg-gray-100 outline-none border border-gray-200 cursor-not-allowed tracking-widest"
                    />
                    <div className="flex items-center gap-1.5 px-4 py-[14px] rounded-[12px] bg-green-50 border border-green-200 shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span className="text-[13px] font-bold text-green-700">Verified</span>
                    </div>
                  </div>
                ) : (
                  <AadhaarKycWidget
                    dob={form.dob}
                    onVerified={() => {
                      setAadhaarVerified(true);
                      fetchDigioStatus();
                    }}
                    onSigningUrl={(url) => {
                      setSigningUrl(url);
                      setShowKycModal(true);
                    }}
                    onAutoFill={(data) => {
                      setForm((prev) => ({
                        ...prev,
                        ...(data.name    && { name: data.name }),
                        ...(data.dob     && { dob: data.dob }),
                        ...(data.aadhaar && { aadhaar: data.aadhaar }),
                      }));
                      setAadhaarVerified(true);
                        fetchDigioStatus();
                    }}
                  />
                )}
              </div>

              {/* PAN + Verify */}
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">PAN Card Number</label>
                {digioStatus?.pan?.verified ? (
                  <div className="flex items-center gap-2">
                    <input
                      name="pan"
                      value={form.pan}
                      readOnly
                      className="flex-1 rounded-[12px] p-[14px] text-[14px] text-[#333] bg-gray-100 outline-none border border-gray-200 uppercase cursor-not-allowed"
                    />
                    <div className="flex items-center gap-1.5 px-4 py-[14px] rounded-[12px] bg-green-50 border border-green-200 shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span className="text-[13px] font-bold text-green-700">Verified</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      name="pan"
                      value={form.pan}
                      onChange={handleChange}
                      maxLength={10}
                      placeholder="ABCDE1234F"
                      className="flex-1 rounded-[12px] p-[14px] text-[14px] text-[#333] bg-white placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors uppercase"
                    />
                    <button
                      type="button"
                      onClick={verifyPan}
                      disabled={panVerified || panLoading || !digioStatus?.aadhaar?.verified}
                      className={`shrink-0 px-4 rounded-[12px] text-[13px] font-bold text-white transition-all
                        ${ panVerified
                          ? "bg-green-500 cursor-not-allowed"
                          : !digioStatus?.aadhaar?.verified
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 cursor-pointer"
                        } disabled:opacity-70`}
                      title={!digioStatus?.aadhaar?.verified ? "Please verify Aadhaar first" : ""}
                    >
                      {panLoading ? "Verifying..." : panVerified ? "Verified ✅" : "Verify PAN"}
                    </button>
                  </div>
                )}
                {panMsg && (
                  <p className={`mt-1.5 text-[12px] font-medium ${ panMsg.type === "success" ? "text-green-600" : "text-red-500" }`}>
                    {panMsg.text}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Full Name (As per PAN)</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter owner's full name"
                  readOnly={!!(digioStatus?.aadhaar?.data?.name || digioStatus?.pan?.data?.name)}
                  className={`w-full rounded-[12px] p-[14px] text-[14px] text-[#333] placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors ${
                    (digioStatus?.aadhaar?.data?.name || digioStatus?.pan?.data?.name) ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                  }`}
                />
              </div>
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Date of Birth</label>
                <DatePicker
                  value={form.dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  readOnly={!!(digioStatus?.aadhaar?.data?.dob || digioStatus?.pan?.data?.dob)}
                  className={`w-full rounded-[12px] p-[14px] text-[14px] text-[#333] outline-none border border-gray-200 focus:border-violet-400 transition-colors name=dob ${
                    (digioStatus?.aadhaar?.data?.dob || digioStatus?.pan?.data?.dob) ? "bg-gray-100" : "bg-white"
                  }`}
                />
              </div>
            </div>

          </div>
        );

      case 2:
        return (
          <div className="bg-[#F3F4F5] rounded-2xl px-5 pt-5 pb-6 mb-5">
            {sectionHeader(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9b59d4" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>,
              "Business Details",
              "Commercial and operational information"
            )}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {/* GST + Verify */}
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">GST Number</label>
                {digioStatus?.gst?.verified ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={form.gstNumber}
                      readOnly
                      className="flex-1 rounded-[12px] p-[14px] text-[14px] text-[#333] bg-gray-100 outline-none border border-gray-200 uppercase cursor-not-allowed"
                    />
                    <div className="flex items-center gap-1.5 px-4 py-[14px] rounded-[12px] bg-green-50 border border-green-200 shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span className="text-[13px] font-bold text-green-700">Verified</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      name="gstNumber"
                      value={form.gstNumber}
                      onChange={handleChange}
                      maxLength={15}
                      placeholder="22AAAAA0000A1Z5"
                      className="flex-1 rounded-[12px] p-[14px] text-[14px] text-[#333] bg-white placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors uppercase"
                    />
                    <button
                      type="button"
                      onClick={verifyGst}
                      disabled={gstVerified || gstLoading}
                      className={`shrink-0 px-4 rounded-[12px] text-[13px] font-bold text-white transition-all
                        ${ gstVerified
                          ? "bg-green-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 cursor-pointer"
                        } disabled:opacity-70`}
                    >
                      {gstLoading ? "Verifying..." : gstVerified ? "Verified ✅" : "Verify GST"}
                    </button>
                  </div>
                )}
                {gstMsg && (
                  <p className={`mt-1.5 text-[12px] font-medium ${ gstMsg.type === "success" ? "text-green-600" : "text-red-500" }`}>
                    {gstMsg.text}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Business Type</label>
                <input
                  name="businessType"
                  value={form.businessType}
                  onChange={handleChange}
                  placeholder="Business type"
                  readOnly={!!digioStatus?.gst?.verified}
                  className={`w-full rounded-[12px] p-[14px] text-[14px] text-[#333] outline-none border border-gray-200 focus:border-violet-400 transition-colors ${
                    digioStatus?.gst?.verified ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                  }`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-y-4 mt-4">
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Shop/Business Name</label>
                <input
                  name="businessName"
                  value={form.businessName}
                  onChange={handleChange}
                  placeholder="Enter registered business name"
                  readOnly={!!digioStatus?.gst?.verified}
                  className={`w-full rounded-[12px] p-[14px] text-[14px] text-[#333] placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors ${
                    digioStatus?.gst?.verified ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                  }`}
                />
              </div>
              <div>
                <label className="block text-[14px] font-semibold text-[#191C1D] mb-1.5">Business Address</label>
                <input
                  name="businessAddress"
                  value={form.businessAddress}
                  onChange={handleChange}
                  placeholder="Shop No, Street, Landmark, City, State, PIN"
                  readOnly={!!digioStatus?.gst?.verified}
                  className={`w-full rounded-[12px] p-[14px] text-[14px] text-[#333] placeholder:text-gray-400 outline-none border border-gray-200 focus:border-violet-400 transition-colors ${
                    digioStatus?.gst?.verified ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                  }`}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="bg-[#F3F4F5] rounded-2xl px-5 pt-5 pb-6 mb-5">
            {kycStatus?.kyc_status === "approved" ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p className="text-[20px] font-bold text-green-700">KYC Already Approved</p>
                <p className="text-[14px] text-[#4C444E] text-center">Your KYC has been verified and approved. No further action is required.</p>
              </div>
            ) : (
              <KycSection onFilesChange={(f) => { kycFilesRef.current = f; setKycFiles(f); }} onLivenessVerified={() => setLivenessVerified(true)} requestedDocs={kycStatus?.reupload_request?.status === "pending" ? kycStatus.reupload_request.documents : null} />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>

      <div className="flex-1 flex flex-col bg-[#F8F9FA] overflow-hidden">

        <div className="flex-1 px-8 py-7 overflow-y-auto">
          <h1 className="text-4xl font-extrabold text-[#34094D] mb-1.5">Onboard New Merchant</h1>
          <p className="text-[16px] text-[#4C444E] leading-relaxed mb-6">
            Complete the three-step digital onboarding process and submit your details. Your<br />
            merchant account will be activated after admin approval.
          </p>

          <div className="rounded-2xl px-7 pt-7 pb-6">
            {renderStepIndicator()}
            {renderStepContent()}

            <div className="flex justify-between mt-2">
              {currentStep === 3 ? (
                <button
                  onClick={() => {}}
                  className="px-11 py-2.5 rounded-lg text-[15px] font-bold border-[1px] border-[#CEC3D0] text-[#191C1D] hover:bg-gray-100 cursor-pointer transition-all"
                >
                  Save as Draft
                </button>
              ) : (
                <button
                  onClick={handlePrev}
                  disabled={currentStep === 1}
                  className={`px-11 py-2.5 rounded-lg text-[15px] font-bold transition-all
                    ${currentStep === 1
                      ? "bg-[#f0f0f0] text-gray-400 cursor-not-allowed"
                      : "bg-gray-500 text-white hover:bg-gray-600 cursor-pointer"}`}
                >
                  Previous
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={loading || (currentStep === 1 && !panVerified) || (currentStep === 2 && !gstVerified) || (currentStep === 3 && !(kycStatus?.reupload_request?.status === "pending") && !livenessVerified)}
                className={`px-11 py-2.5 rounded-lg text-[15px] font-bold text-white bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 active:scale-[0.99] transition-all flex items-center gap-2
                  ${ (loading || (currentStep === 1 && !panVerified) || (currentStep === 2 && !gstVerified) || (currentStep === 3 && !(kycStatus?.reupload_request?.status === "pending") && !livenessVerified))
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                  }`}
              >
                {loading && <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>}
                {loading ? "Submitting…" : currentStep === 3 ? "Submit For Approval" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* KYC Iframe Component */}
      <KycIframe 
        isOpen={showKycModal}
        signingUrl={signingUrl}
        onClose={closeKycModal}
      />
    </>
  );
}
